import { Fragment } from "react";
import moment from "moment-timezone";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { QueueJob } from "@/admin/entities/queues";
import type { QueueJobQueryQueryType } from "@/types/graphql/admin.generated";
import { JSONView } from "./JSONView";
import { ProgressCell } from "./ProgressCell";

interface QueueJobDetailsProps {
  job?: QueueJob;
  editData?: unknown;
  id?: string;
}

const DATE_FORMAT = "DD.MM.YYYY в HH:mm:ss";

/** Служебные ключи Apollo в JSON-просмотре только мешают */
function removeTypename(value: unknown) {
  if (!value) return null;
  return JSON.parse(JSON.stringify(value), (key, parsed) =>
    key === "__typename" ? undefined : parsed
  );
}

function getInfo(job: QueueJob) {
  const info: { label: string; value: React.ReactNode }[] = [
    { label: "ID", value: job.id },
    { label: "Название", value: job.name },
    { label: "Попыток", value: job.attemptsMade },
  ];

  if (job.timestamp) {
    info.push({
      label: "Создана",
      value: moment(job.timestamp).format(DATE_FORMAT),
    });
  }

  if (job.processedOn) {
    info.push({
      label: "Взята в работу",
      value: moment(job.processedOn).format(DATE_FORMAT),
    });
  }

  if (job.finishedOn) {
    info.push({
      label: "Завершена",
      value: moment(job.finishedOn).format(DATE_FORMAT),
    });
  }

  if (job.failedReason) {
    info.push({ label: "Причина ошибки", value: job.failedReason });
  }

  return info;
}

/**
 * Карточка задачи очереди: прогресс, таблица атрибутов и разворачивающиеся
 * блоки с данными, стектрейсом, логами, результатом и опциями.
 */
export function QueueJobDetails({
  job: jobProp,
  editData,
}: QueueJobDetailsProps) {
  const job = jobProp || (editData as QueueJobQueryQueryType | undefined)?.job;

  if (!job) return null;

  const stacktrace = (job.stacktrace || []).filter(
    (row): row is string => typeof row === "string"
  );
  const logs = (job.logs || []).filter(
    (row): row is string => typeof row === "string"
  );

  return (
    <div style={{ overflow: "hidden" }}>
      <Toolbar>
        <Typography
          variant="h6"
          color="inherit"
          style={{ marginRight: "auto" }}
        >
          {`#${job.id} – ${job.state}`}
        </Typography>
      </Toolbar>
      <Grid container>
        <Grid size={12}>
          <ProgressCell
            progress={job.progress}
            steps={job.steps}
            state={job.state}
          />
        </Grid>
        <Grid size={12}>
          <Table size="small">
            <TableBody>
              {getInfo(job).map(({ label, value }, key) => (
                <TableRow key={key}>
                  <TableCell component="th" scope="row" variant="head">
                    {label}
                  </TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
        <Grid size={12}>
          <div>
            <Accordion elevation={0} square defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} id="panel-data">
                <Typography variant="subtitle2">Данные</Typography>
              </AccordionSummary>
              <AccordionDetails style={{ padding: 0, display: "block" }}>
                <JSONView src={job.data} />
              </AccordionDetails>
            </Accordion>

            {stacktrace.length > 0 && (
              <Accordion elevation={0} square>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  id="panel-stacktrace"
                >
                  <Typography variant="subtitle2">Стектрейс</Typography>
                </AccordionSummary>
                <AccordionDetails style={{ display: "block", padding: 0 }}>
                  {stacktrace.map((trace, key) => (
                    <Fragment key={key}>
                      {!!key && <Divider />}
                      <div style={{ padding: 16 }}>
                        {trace.split("\n").map((row, i) => (
                          <Typography
                            key={i}
                            color={i ? "textSecondary" : "error"}
                            variant="body1"
                            style={{ whiteSpace: "pre-wrap" }}
                          >
                            {row}
                          </Typography>
                        ))}
                      </div>
                    </Fragment>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {logs.length > 0 && (
              <Accordion elevation={0} square>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  id="panel-logs"
                >
                  <Typography variant="subtitle2">Логи</Typography>
                </AccordionSummary>
                <AccordionDetails style={{ display: "block" }}>
                  {logs.map((log, i) => (
                    <Typography variant="body2" gutterBottom key={i}>
                      {log}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {job.result && (
              <Accordion elevation={0} square>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  id="panel-result"
                >
                  <Typography variant="subtitle2">Результат</Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: 0, display: "block" }}>
                  <JSONView src={job.result} />
                </AccordionDetails>
              </Accordion>
            )}

            {job.opts && (
              <Accordion elevation={0} square>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  id="panel-opts"
                >
                  <Typography variant="subtitle2">Опции</Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: 0, display: "block" }}>
                  <JSONView src={removeTypename(job.opts)} />
                </AccordionDetails>
              </Accordion>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  );
}
